import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, AlertCircle, Ban, RefreshCw, Plus, ChevronDown, ChevronUp, Square } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const M = {
  gold: "#E4B23C",
  goldSoft: "rgba(228,178,60,0.12)",
  goldLine: "rgba(228,178,60,0.35)",
  panel: "var(--bc-panel, #09090B)",
  elev: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))",
  text: "var(--bc-ink, #FFFFFF)",
  muted: "var(--bc-sub, #71717A)",
  red: "#EF4444",
  green: "#22C55E",
};

const Label = ({ children }) => (
  <span style={{
    fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
    letterSpacing: "0.16em", textTransform: "uppercase", color: M.muted,
  }}>{children}</span>
);

export default function MonolitePanel({ fixtureId, onAddPick }) {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); // {verdict, model, generated_at, duration_s}
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [added, setAdded] = useState(false);
  const [showRejected, setShowRejected] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/match/${fixtureId}/monolite`);
      if (res.data?.result) setResult(res.data.result);
      if (res.data?.status === "running") { setRunning(true); return "running"; }
      if (res.data?.status === "error") { setError(res.data.error); setRunning(false); return "error"; }
      setRunning(false);
      return res.data?.status;
    } catch {
      return null;
    }
  }, [fixtureId]);

  useEffect(() => {
    if (!isOwner) return;
    setResult(null); setError(null); setRunning(false); setOpen(false);
    loadStatus().then((st) => { if (st === "running") setOpen(true); });
  }, [isOwner, fixtureId, loadStatus]);

  // Poll while a job runs
  useEffect(() => {
    if (!running || !isOwner) { setElapsed(0); return; }
    const t0 = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    const poll = setInterval(async () => {
      const st = await loadStatus();
      if (st && st !== "running") { clearInterval(poll); }
    }, 5000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [running, isOwner, loadStatus]);

  const start = async (force) => {
    if (!isOwner || running) return;
    setError(null); setAdded(false);
    setOpen(true);
    try {
      const res = await axios.post(`${API}/admin/match/${fixtureId}/monolite`, {
        force: !!force,
      });
      if (res.data?.status === "cached" && !force) {
        setResult(res.data);
        return;
      }
      setResult(null);
      setRunning(true);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Avvio analisi fallito");
    }
  };

  if (!isOwner) return null;

  const v = result?.verdict || null;
  const isNoBet = v && (v.verdict || "").toUpperCase() === "NO_BET";
  const accent = isNoBet ? M.red : M.gold;

  const handleAdd = () => {
    if (!v || isNoBet || !onAddPick) return;
    onAddPick({
      bet: v.bet,
      prob: Number(v.probability) || 0,
      type: v.type === "combo" ? "combo" : (v.market_family || "single"),
      source: "monolite",
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  return (
    <div data-testid="monolite-block" style={{ marginBottom: "0.25rem" }}>
      {/* Trigger */}
      <button
        data-testid="monolite-open-btn"
        onClick={() => (open && (result || error) ? setOpen(false) : start(false))}
        disabled={running}
        style={{
          display: "flex", alignItems: "center", gap: "0.7rem",
          padding: "0.8rem 1rem", width: "100%",
          background: `linear-gradient(90deg, ${M.goldSoft}, transparent 65%)`,
          border: `1px solid ${M.goldLine}`,
          borderLeft: `3px solid ${M.gold}`,
          color: M.gold,
          fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: "0.9rem", letterSpacing: "0.2em",
          textTransform: "uppercase", fontWeight: 700,
          cursor: running ? "wait" : "pointer",
          transition: "background-color 0.2s ease, border-color 0.2s ease",
        }}
      >
        {running ? <Loader2 size={14} className="animate-spin" /> : <Square size={13} strokeWidth={3} />}
        MONOLITE
        <span style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
          letterSpacing: "0.12em", color: "rgba(255,255,255,0.45)",
          textTransform: "none",
        }}>
          {running
            ? `Claude Opus 5 sta valutando tutti i mercati… ${elapsed}s`
            : "Un solo pronostico · quello con l'affidabilità massima"}
        </span>
        <span style={{
          marginLeft: "auto", fontFamily: "'JetBrains Mono',monospace",
          fontSize: "0.6rem", letterSpacing: "0.14em", color: "rgba(255,255,255,0.35)",
        }}>
          ADMIN · OPUS 5 · QUOTA MIN 1.30        </span>
      </button>

      {/* Panel */}
      {open && (running || result || error) && (
        <div data-testid="monolite-panel" style={{
          background: M.panel, border: `1px solid ${M.border}`, borderTop: "none",
          padding: "1rem",
        }}>
          {running && (
            <div data-testid="monolite-running" style={{
              display: "flex", alignItems: "center", gap: "0.6rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem", color: M.gold,
            }}>
              <Loader2 size={13} className="animate-spin" />
              Analisi onnicomprensiva in corso — 1-4 minuti. Puoi continuare a navigare, il risultato resta salvato.
            </div>
          )}

          {error && !running && (
            <div data-testid="monolite-error" style={{
              display: "flex", alignItems: "center", gap: "0.5rem",
              color: M.red, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.75rem",
            }}>
              <AlertCircle size={13} /> {error}
            </div>
          )}

          {v && !running && (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
              {/* Verdict headline */}
              <div style={{
                display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap",
                background: M.elev, border: `1px solid ${accent}44`,
                borderLeft: `3px solid ${accent}`, padding: "0.85rem 1rem",
              }}>
                {isNoBet ? (
                  <Ban size={18} style={{ color: M.red, flexShrink: 0 }} />
                ) : (
                  <Square size={16} strokeWidth={3} style={{ color: M.gold, flexShrink: 0 }} />
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                  <Label>{isNoBet ? "Verdetto" : "Pronostico unico"}</Label>
                  <span data-testid="monolite-bet" style={{
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                    fontSize: "1.5rem", letterSpacing: "0.04em",
                    textTransform: "uppercase", color: isNoBet ? M.red : M.text,
                    lineHeight: 1.1,
                  }}>
                    {isNoBet ? "NO BET" : v.bet}
                  </span>
                </div>
                <div style={{ display: "flex", gap: "1.4rem", marginLeft: "auto", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <Label>Affidabilità</Label>
                    <span data-testid="monolite-confidence" style={{
                      fontFamily: "'JetBrains Mono',monospace", fontSize: "1.05rem",
                      color: isNoBet ? M.red : M.gold, fontWeight: 700,
                    }}>
                      {Math.round(Number(v.confidence_score) || 0)}/100
                      <span style={{ fontSize: "0.6rem", marginLeft: 6, color: M.muted }}>
                        {v.confidence_label || ""}
                      </span>
                    </span>
                  </div>
                  {!isNoBet && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Label>Probabilità</Label>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.05rem", color: M.text }}>
                          {Number(v.probability || 0).toFixed(1)}%
                        </span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Label>Quota equa</Label>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.05rem", color: M.text }}>
                          {Number(v.fair_odds || 0).toFixed(2)}
                        </span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Label>Quota minima</Label>
                        <span data-testid="monolite-min-odds" style={{
                          fontFamily: "'JetBrains Mono',monospace", fontSize: "1.05rem", color: M.green,
                        }}>
                          {Number(v.min_odds || 0).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {isNoBet && v.no_bet_reason && (
                <p data-testid="monolite-no-bet-reason" style={{
                  fontSize: "0.82rem", color: M.text, lineHeight: 1.6, margin: 0,
                }}>
                  {v.no_bet_reason}
                </p>
              )}

              {/* Riassunto motivi (2-3 righe) */}
              {(v.summary || (Array.isArray(v.reasoning) && v.reasoning.length > 0)) && (
                <div data-testid="monolite-reasoning">
                  <Label>Perché questo pronostico</Label>
                  <p style={{ margin: "0.35rem 0 0", fontSize: "0.84rem", color: M.text, lineHeight: 1.6 }}>
                    {v.summary || v.reasoning.join(" ")}
                  </p>
                </div>
              )}

              {/* Risks */}
              {Array.isArray(v.risks) && v.risks.length > 0 && (
                <div data-testid="monolite-risks">
                  <Label>Rischi</Label>
                  <ul style={{ margin: "0.4rem 0 0", paddingLeft: "1.1rem", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                    {v.risks.map((r, i) => (
                      <li key={i} style={{ fontSize: "0.8rem", color: "#F59E0B", lineHeight: 1.5 }}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Rejected markets */}
              {Array.isArray(v.rejected) && v.rejected.length > 0 && (
                <div>
                  <button
                    data-testid="monolite-toggle-rejected"
                    onClick={() => setShowRejected((s) => !s)}
                    style={{
                      display: "flex", alignItems: "center", gap: "0.4rem",
                      background: "transparent", border: "none", padding: 0,
                      color: M.muted, cursor: "pointer",
                      fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem",
                      letterSpacing: "0.14em", textTransform: "uppercase",
                    }}
                  >
                    {showRejected ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    Mercati scartati ({v.rejected.length})
                  </button>
                  {showRejected && (
                    <div data-testid="monolite-rejected-list" style={{
                      marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "0.4rem",
                    }}>
                      {v.rejected.map((r, i) => (
                        <div key={i} style={{
                          background: M.elev, border: `1px solid ${M.border}`,
                          padding: "0.5rem 0.7rem",
                        }}>
                          <span style={{
                            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                            textTransform: "uppercase", letterSpacing: "0.08em",
                            color: M.text, fontSize: "0.8rem",
                          }}>{r.bet}</span>
                          <p style={{ margin: "0.2rem 0 0", fontSize: "0.76rem", color: M.muted, lineHeight: 1.5 }}>
                            {r.why}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(v.data_gaps) && v.data_gaps.length > 0 && (
                <p data-testid="monolite-data-gaps" style={{
                  margin: 0, fontFamily: "'JetBrains Mono',monospace",
                  fontSize: "0.68rem", color: M.muted,
                }}>
                  Dati mancanti: {v.data_gaps.join(" · ")}
                </p>
              )}

              {/* Footer actions */}
              <div style={{
                display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap",
                borderTop: `1px solid ${M.border}`, paddingTop: "0.7rem",
              }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem", color: M.muted }}>
                  {result?.model || "claude-opus-5"}
                  {result?.generated_at ? ` · ${new Date(result.generated_at).toLocaleString("it-IT")}` : ""}
                  {result?.duration_s ? ` · ${Math.round(result.duration_s)}s` : ""}
                </span>
                <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {!isNoBet && (
                    <button
                      data-testid="monolite-add-pick-btn"
                      onClick={handleAdd}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: "0.35rem",
                        background: added ? "rgba(34,197,94,0.15)" : M.goldSoft,
                        border: `1px solid ${added ? M.green : M.goldLine}`,
                        color: added ? M.green : M.gold,
                        padding: "0.45rem 0.8rem", cursor: "pointer",
                        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.63rem",
                        letterSpacing: "0.12em", textTransform: "uppercase",
                      }}
                    >
                      <Plus size={12} /> {added ? "Aggiunto" : "Aggiungi ai miei pronostici"}
                    </button>
                  )}
                  <button
                    data-testid="monolite-regenerate-btn"
                    onClick={() => start(true)}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "0.35rem",
                      background: "transparent", border: `1px solid ${M.border}`,
                      color: M.muted, padding: "0.45rem 0.8rem", cursor: "pointer",
                      fontFamily: "'JetBrains Mono',monospace", fontSize: "0.63rem",
                      letterSpacing: "0.12em", textTransform: "uppercase",
                    }}
                  >
                    <RefreshCw size={12} /> Rigenera
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
